import { useCallback, useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { useI18n } from "./i18n";
import { loadCatalog, loadMemberships, loadMyInvites, loadWorkspace, type MyInvite } from "./data";
import { Join } from "./screens/Join";
import { SignIn } from "./screens/SignIn";
import { Setup } from "./screens/Setup";
import { Shell } from "./screens/Shell";
import type { Module, Preset, Workspace } from "./types";

type State =
  | { kind: "loading"; step: string }
  | { kind: "signed-out" }
  | { kind: "setup"; catalog: Module[]; presets: Preset[] }
  | { kind: "join"; invites: MyInvite[]; catalog: Module[]; presets: Preset[] }
  | { kind: "ready"; ws: Workspace }
  | { kind: "error"; message: string };

/** Rejects if the promise takes longer than `ms`, naming the step that stalled. */
function withTimeout<T>(p: Promise<T>, ms: number, step: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`Timed out while ${step}.`)), ms);
    p.then(
      (v) => { window.clearTimeout(timer); resolve(v); },
      (e) => { window.clearTimeout(timer); reject(e); },
    );
  });
}

/** Removes a stored sign-in from this browser without calling the server. */
function clearLocalSession() {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith("sb-") || k.startsWith("peoplecore-auth")) localStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

export default function App() {
  const { t } = useI18n();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [state, setState] = useState<State>({ kind: "loading", step: "checking your sign-in" });
  const [slow, setSlow] = useState(false);
  const loadId = useRef(0);

  useEffect(() => {
    if (!supabase) {
      setState({ kind: "error", message: "The database connection is not configured." });
      return;
    }
    withTimeout(supabase.auth.getSession(), 10000, "checking your sign-in")
      .then(({ data }) => setSession(data.session))
      .catch((err: Error) => setState({ kind: "error", message: err.message }));
    // Only record the new session here. Database calls made inside this callback can lock up
    // Supabase's sign-in handling, so the loading happens in a separate effect.
    const { data } = supabase.auth.onAuthStateChange((_event, s) => {
      window.setTimeout(() => setSession(s), 0);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const load = useCallback(async (s: Session) => {
    const id = ++loadId.current;
    const step = (name: string) => id === loadId.current && setState({ kind: "loading", step: name });
    try {
      step("loading your companies");
      const [memberships, catalog] = await withTimeout(
        Promise.all([loadMemberships(s.user.id), loadCatalog()]),
        15000,
        "loading your companies",
      );
      if (id !== loadId.current) return;
      if (memberships.length === 0) {
        const invites = await loadMyInvites().catch(() => [] as MyInvite[]);
        if (id !== loadId.current) return;
        setState(invites.length > 0
          ? { kind: "join", invites, catalog: catalog.modules, presets: catalog.presets }
          : { kind: "setup", catalog: catalog.modules, presets: catalog.presets });
        return;
      }
      step("loading your workspace");
      const ws = await withTimeout(loadWorkspace(memberships[0], catalog.modules), 15000, "loading your workspace");
      if (id === loadId.current) setState({ kind: "ready", ws });
    } catch (err) {
      if (id === loadId.current) setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  // Changes when sign-in is first checked, on sign-in, on sign-out and on switching user.
  const sessionKey = session === undefined ? "unknown" : session === null ? "signed-out" : session.user.id;
  useEffect(() => {
    if (session === undefined) return;
    if (session === null) {
      loadId.current++;
      setState({ kind: "signed-out" });
      return;
    }
    void load(session);
  }, [sessionKey, load]); // eslint-disable-line react-hooks/exhaustive-deps

  // If loading drags on, offer a way out instead of an endless spinner.
  useEffect(() => {
    setSlow(false);
    if (state.kind !== "loading") return;
    const timer = window.setTimeout(() => setSlow(true), 8000);
    return () => window.clearTimeout(timer);
  }, [state.kind]);

  const signOut = () => {
    window.location.hash = "";
    clearLocalSession();
    setSession(null);
    void supabase?.auth.signOut().catch(() => undefined);
  };
  const reload = () => {
    if (session) void load(session);
  };

  switch (state.kind) {
    case "loading":
      return (
        <div className="center">
          <p className="muted">{t("loading")}</p>
          {slow && (
            <>
              <p className="muted small-note">Still {state.step}…</p>
              <div className="row">
                <button className="btn secondary small" onClick={() => window.location.reload()}>Try again</button>
                <button className="btn secondary small" onClick={signOut}>{t("signOut")}</button>
              </div>
            </>
          )}
        </div>
      );
    case "signed-out":
      return <SignIn />;
    case "join":
      return <Join invites={state.invites} onJoined={reload} onSignOut={signOut}
        onSetUp={() => setState({ kind: "setup", catalog: state.catalog, presets: state.presets })} />;
    case "setup":
      return <Setup catalog={state.catalog} presets={state.presets} onCreated={reload} onSignOut={signOut} />;
    case "ready":
      return <Shell ws={state.ws} onSignOut={signOut} onModulesChanged={reload} />;
    case "error":
      return (
        <div className="center">
          <p className="alert danger" role="alert">{state.message}</p>
          <div className="row">
            <button className="btn secondary" onClick={() => window.location.reload()}>Try again</button>
            <button className="btn secondary" onClick={signOut}>{t("signOut")}</button>
          </div>
        </div>
      );
  }
}
